// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HeaderMyAccountMappedLink } from "@electrowire/Overrides/Widgets/Common/HeaderSignIn";
import ChevronRight from "@gcg/XDL/Icons/ChevronRight";
import { color } from "@gcg/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    links?: HeaderMyAccountMappedLink[];
    onSignOutHandler?: () => void;
}

type Props = OwnProps;

interface MiniMyAccountStyles {
    container?: InjectableCss;
    header?: InjectableCss;
    headerLink?: LinkPresentationProps;
    myAccountItemsContainer?: InjectableCss;
    myAccountItemGroupText?: TypographyPresentationProps;
    myAccountItemLink?: LinkPresentationProps;
    loggedInWrapper?: InjectableCss;
    signedOutWrapper?: InjectableCss;
    loggedInHeader?: TypographyPresentationProps;
    billToInformationWrapper?: InjectableCss;
    billToInformation?: InjectableCss;
    billToLogoWrapper?: InjectableCss;
    billToCompanyName?: TypographyPresentationProps;
    billToAccountNumber?: TypographyPresentationProps;
    billToLogoImg?: ImgProps;
    signOutButton?: ButtonPresentationProps;
}

const miniMyAccountStyles: MiniMyAccountStyles = {
    header: {
        css: css`
            margin: 12px 16px 8px;
            padding-bottom: 8px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    headerLink: {
        color: "text.main",
        typographyProps: {
            size: 20,
        },
    },
    myAccountItemsContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            gap: 7px;
            margin: 0 16px 38px;
            & > div {
                flex: 1 1 calc(50% - 8px);
            }
        `,
    },
    myAccountItemGroupText: {
        color: "text.main",
        size: 16,
    },
    myAccountItemLink: {
        color: "primary",
        icon: {
            iconProps: {
                size: 8,
                src: ChevronRight,
                color: "primary",
            },
        },
        typographyProps: {
            size: 14,
        },
    },
    loggedInWrapper: {
        css: css`
            margin: 0 16px 36px;
        `,
    },
    loggedInHeader: {
        size: 16,
        css: css`
            display: block;
            letter-spacing: 0;
            line-height: 20px;
            margin-bottom: 8px;
            padding-bottom: 8px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    billToInformationWrapper: {
        css: css`
            display: flex;
            gap: 8px;
        `,
    },
    billToInformation: {
        css: css`
            flex: 1 1 calc(50% - 8px);
        `,
    },
    billToCompanyName: {
        size: 16,
        weight: "bold",
        css: css`
            display: block;
            letter-spacing: 0.5px;
            line-height: 19px;
            margin-bottom: 10px;
        `,
    },
    billToAccountNumber: {
        size: 14,
        weight: "normal",
        css: css`
            display: block;
            letter-spacing: 0;
            line-height: 20px;
            margin-bottom: 0;
        `,
    },
    billToLogoWrapper: {
        css: css`
            flex: 1 1 calc(50% - 8px);
            max-width: 140px;
            max-height: 57px;
        `,
    },
    billToLogoImg: {
        css: css`
            max-width: 100%;
            max-height: 100%;
            height: auto;
            width: auto;
        `,
    },
    signedOutWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            background: ${color("common.ui3")};
            padding: 16px;
        `,
    },
    signOutButton: {
        variant: "primary",
        css: css`
            min-width: 160px;
        `,
    },
};

const styles = miniMyAccountStyles;

const MiniMyAccount: FC<Props> = ({ links, onSignOutHandler }) => {
    const { myAccountPageUrl, billToStateValue, isPunchOutSession } = useAppSelector(state => ({
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        billToStateValue: getBillToState(state, state.context.session.billToId).value,
        isPunchOutSession: getIsPunchOutSession(state),
    }));

    return (
        <StyledWrapper {...styles.container} data-test-selector="MiniMyAccount">
            <StyledWrapper {...styles.header}>
                <Link href={myAccountPageUrl} {...styles.headerLink}>
                    {translate("My Account")}
                </Link>
            </StyledWrapper>
            {links && (
                <StyledWrapper {...styles.myAccountItemsContainer}>
                    {links.map(link => (
                        <StyledWrapper key={link.id}>
                            {link?.isGroup ? (
                                <Typography {...styles.myAccountItemGroupText}>
                                    {translate(link.title || "")}
                                </Typography>
                            ) : (
                                <Link href={link.url} {...styles.myAccountItemLink}>
                                    {link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.loggedInWrapper}>
                <Typography {...styles.loggedInHeader}>{translate("Logged in as")}:</Typography>
                <StyledWrapper {...styles.billToInformationWrapper}>
                    <StyledWrapper {...styles.billToInformation}>
                        <Typography {...styles.billToCompanyName}>{billToStateValue?.companyName}</Typography>
                        <Typography {...styles.billToAccountNumber}>
                            {translate("Account #")}: {billToStateValue?.properties["soldTo"]}
                        </Typography>
                    </StyledWrapper>
                    {billToStateValue?.properties["logoImagePath"] && (
                        <StyledWrapper {...styles.billToLogoWrapper}>
                            <Img
                                src={billToStateValue?.properties["logoImagePath"]}
                                alt={billToStateValue?.companyName}
                                {...styles.billToLogoImg}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.signedOutWrapper}>
                <Button {...styles.signOutButton} onClick={onSignOutHandler} data-test-selector="headerSignOutLink">
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
